'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, Heart, Zap, X } from 'lucide-react'

export default function LunaHomepageModal() {
  const router = useRouter()
  const [isVisible, setIsVisible] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    // Show modal on every refresh/page load (for non-signed-in users)
    // Small delay for better UX
    const timer = setTimeout(() => {
      setIsOpen(true)
      setIsVisible(true)
    }, 1000)
    
    return () => clearTimeout(timer)
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      setIsOpen(false)
      // Don't persist - modal will show again on next refresh
    }, 300)
  }

  const handleSignIn = () => {
    handleClose()
    router.push('/sign-in')
  }

  const handleSignUp = () => {
    handleClose()
    router.push('/sign-up')
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isVisible ? 0.95 : 0 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-gradient-to-br from-purple-900 via-pink-900 to-indigo-900 backdrop-blur-md"
            onClick={handleClose}
          />

          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-pink-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
            <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-indigo-500/20 rounded-full blur-3xl animate-pulse delay-500" />
          </div>

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: isVisible ? 1 : 0.9, opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative bg-gradient-to-br from-purple-900/95 via-pink-900/95 to-indigo-900/95 backdrop-blur-xl rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 max-w-2xl w-full border-2 border-purple-400/50"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full z-10"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Sparkle Effects */}
            <div className="absolute top-2 right-8 sm:top-4 sm:right-12">
              <Sparkles className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-400 animate-pulse" />
            </div>
            <div className="absolute top-4 left-4 sm:top-8 sm:left-8">
              <Sparkles className="w-3 h-3 sm:w-5 sm:h-5 text-pink-400 animate-pulse delay-500" />
            </div>

            {/* Luna Image Container */}
            <div className="flex justify-center mb-4 sm:mb-6 md:mb-8 relative">
              <div className="relative">
                {/* Glowing Ring */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 blur-lg sm:blur-xl animate-pulse opacity-75" />
                
                {/* Image Container */}
                <div className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 rounded-full overflow-hidden border-2 sm:border-4 border-purple-300/50 shadow-2xl transform hover:scale-105 transition-transform duration-300">
                  <Image
                    src="/images/luna.webp"
                    alt="Luna - Your AI Companion"
                    fill
                    className="object-cover"
                    priority
                    sizes="(max-width: 640px) 128px, (max-width: 768px) 160px, (max-width: 1024px) 192px, 224px"
                  />
                </div>
                
                {/* Floating Hearts */}
                <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4">
                  <Heart className="w-5 h-5 sm:w-8 sm:h-8 text-red-400 animate-bounce" />
                </div>
                <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4">
                  <Heart className="w-4 h-4 sm:w-6 sm:h-6 text-pink-400 animate-bounce delay-300" />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="text-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-4 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                <Zap className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-yellow-400 animate-pulse" />
                <span className="whitespace-nowrap">Call Luna with just $1</span>
                <Zap className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-yellow-400 animate-pulse delay-500" />
              </h1>
              
              <div className="bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 border border-purple-300/50">
                <p className="text-purple-100 text-base sm:text-lg md:text-xl lg:text-2xl mb-3 sm:mb-4 font-semibold">
                  Start Your Journey Today!
                </p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mb-3 sm:mb-4">
                  <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 border border-white/20">
                    <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">$1</p>
                    <p className="text-purple-200 text-xs sm:text-sm">One-Time</p>
                  </div>
                  
                  <div className="text-white text-xl sm:text-2xl font-bold hidden sm:block">→</div>
                  <div className="text-white text-lg font-bold sm:hidden">↓</div>
                  
                  <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 border-2 border-yellow-300">
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Free</p>
                    <p className="text-gray-800 text-xs sm:text-sm font-semibold">Minutes!</p>
                  </div>
                </div>
                
                <p className="text-purple-200 text-sm sm:text-base md:text-lg">
                  Get started and enjoy meaningful conversations with Luna!
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8">
                <motion.button
                  onClick={handleSignUp}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl sm:rounded-2xl shadow-lg transform transition-all duration-300 text-base sm:text-lg flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Zap className="w-5 h-5 sm:w-6 sm:h-6" />
                  <span>Sign Up</span>
                </motion.button>
                
                <motion.button
                  onClick={handleSignIn}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl sm:rounded-2xl shadow-lg transform transition-all duration-300 text-base sm:text-lg flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Sign In</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

